/**
 * Alarm log — the full history, not just what is currently ringing.
 *
 * The panel deliberately shows only active alarms, which is right for operating a set but leaves no
 * way to answer "what happened overnight" or "how long were we down". Everything the alarm engine
 * has ever raised is already in the database with its raise, clear and acknowledgement times; this
 * page is the view onto it.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth';
import { ApiError, type AlarmRecord } from '../api';
import { Banner } from '../ui';

const SEVERITY_CLASS: Record<string, string> = {
  critical: 'crit',
  major: 'crit',
  warning: 'warn',
};

/** Human duration. An alarm's length is the thing an operator actually reads off this page. */
function since(fromIso: string, toIso: string | null): string {
  const ms = (toIso ? Date.parse(toIso) : Date.now()) - Date.parse(fromIso);
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'under a minute';
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m`;
  return `${Math.floor(hrs / 24)}d ${hrs % 24}h`;
}

const stamp = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

export function Alarms(): JSX.Element {
  const { api } = useAuth();
  const [rows, setRows] = useState<AlarmRecord[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [status, setStatus] = useState<'active' | 'all'>('all');

  const load = useCallback(() => {
    api
      .listAlarms({ status, limit: 500 })
      .then((r) => setRows(r.alarms))
      .catch((e) => setErr(e instanceof ApiError ? e.message : 'Could not load the alarm log.'));
  }, [api, status]);
  useEffect(load, [load]);

  const activeCount = rows?.filter((a) => !a.cleared_at).length ?? 0;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Alarm log</h1>
          <div className="lead">
            Every alarm raised across the fleet, with how long it lasted
            {rows && (
              <>
                {' '}
                <span className="faint">·</span> {activeCount} active of {rows.length} shown
              </>
            )}
          </div>
        </div>
        <div className="row-right">
          <div className="seg">
            <button
              className={status === 'active' ? 'seg-on' : ''}
              onClick={() => setStatus('active')}
            >
              Active
            </button>
            <button className={status === 'all' ? 'seg-on' : ''} onClick={() => setStatus('all')}>
              All
            </button>
          </div>
        </div>
      </div>

      {err && <Banner kind="err">{err}</Banner>}

      <div className="panel">
        <div className="panel-pad">
          {!rows ? (
            <div className="muted">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="muted">
              {status === 'active'
                ? 'Nothing is in alarm right now.'
                : 'No alarms have been raised yet.'}
            </div>
          ) : (
            <table className="tbl alarm-log">
              <thead>
                <tr>
                  <th>Severity</th>
                  <th>Alarm</th>
                  <th>Site</th>
                  <th>Raised</th>
                  <th>Duration</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id} className={a.cleared_at ? 'cleared' : ''}>
                    <td>
                      <span className={`pill ${SEVERITY_CLASS[a.severity] ?? ''}`}>
                        <span className="dot" />
                        {a.severity}
                      </span>
                    </td>
                    <td>
                      <div className="al-label">{a.label}</div>
                      <div className="al-code mono">
                        {a.code} <span className="faint">·</span> {a.source}
                      </div>
                    </td>
                    <td>
                      {a.controller_id ? (
                        <Link to={`/controllers/${a.controller_id}`}>{a.site_name ?? 'site'}</Link>
                      ) : (
                        <span className="muted">{a.site_name ?? '—'}</span>
                      )}
                      {a.controller_name && <div className="al-code">{a.controller_name}</div>}
                    </td>
                    <td className="mono nowrap">{stamp(a.raised_at)}</td>
                    <td className="nowrap">{since(a.raised_at, a.cleared_at)}</td>
                    <td>
                      {!a.cleared_at ? (
                        <span className="pill crit">
                          <span className="dot" />
                          active
                        </span>
                      ) : (
                        <span className="pill">cleared</span>
                      )}
                      {a.acked_at && (
                        <div className="al-code" title={a.ack_note ?? undefined}>
                          acked
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
